import _ from "lodash";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";

import A5ScreeningDetails from "@/components/Common/A5ScreeningDetails";
import A5ScreeningList from "@/components/Common/A5ScreeningList";
import { ActionSection } from "@/components/Navigation/Toolbar/ActionSection/ActionSection";
import AddAppointment from "@/components/Provider/ScheduleAvailability/AddAppointment";
import { AdvancedCareActivityNewForm } from "@/components/Provider/PatientView/MemberProfile/AdvancedCareActivity/AdvancedCareActivityNewForm";
import AVCheck from "@/components/Common/AVCheck";
import { canCreateAdvancedCareActivityNotes } from "@/lib/helpers/checkACPNoteAccess";
import CareCommunicationForm from "@/components/Provider/PatientView/MemberProfile/CareCommunication/CareCommunicationForm";
import { CareManagementNewForm } from "@/components/Provider/PatientView/MemberProfile/CareManagement/CareManagementNewForm";
import CaseManagementForm from "@/components/Provider/PatientView/MemberProfile/CaseManagement/CaseManagementForm";
import ChangePassword from "@/components/Authentication/ChangePassword";
import DischargeData from "@/components/Provider/PatientView/MemberProfile/DischargeData";
import DraftNotesTable from "@/components/Provider/DraftNotes";
import EditEvent from "@/components/Provider/ScheduleAvailability/EditEvent";
import { Footer } from "@/component-library/Footer";
import GAD7 from "@/features/assessments/GAD7";
import { getProviderProfile, isAuthorized } from "@/redux/actions/auth";
import { Main2 } from "@/ui2/atoms/Main";
import MemberProfile from "@/components/Provider/PatientView/MemberProfile/MemberProfile";
import MessagesView from "@/components/Common/MessagesView/MessagesView";
import PHQ9 from "@/features/assessments/PHQ9";
import PrintNoteView from "@/components/Provider/PatientView/MemberProfile/PrintNoteView";
import PrivacyPolicy from "@/components/Common/PrivacyPolicy";
import { ProtectedRoute } from "@/features/access-control/components/ProtectedRoute";
import { ProviderHome } from "@/portals/provider/home/ProviderHome";
import { redirectOnSignIn } from "@/utils/redirectOnSignIn";
import SystemCheck from "@/components/Common/SystemCheck";
import { Toolbar } from "@/components/Navigation/Toolbar/Toolbar";
import TutorialScreens from "@/components/Common/TutorialScreen";
import { WaitlistForm } from "@/components/Common/WaitlistForm/WaitlistForm";

import mapLabelToComponent from "../common/mapLabelToComponent";
import {
  provider_behavioral,
  provider_case_manager,
  provider_pcp,
  provider_peer,
  providerRoute,
} from "../../../config/roles";
import Settings from "../common/SettingsPage";
import VideoClient from "../../VideoClient";
import { ViewFullNote } from "../common/ViewFullNote/ViewFullNote";

import A5Report from "./A5Report";
import CareTeamList from "./PatientProfileView/CareTeamList";
import ClinicalAssessment from "./PatientProfileView/ClinicalAssessment";
import FileList from "./FileList";
import IBARReport from "./IBARReport";
import IPSRReport from "./IPSRReport";
import NewNote from "./PatientProfileView/NewNote";
import NewNoteV2 from "./PatientProfileView/NewNoteV2";
import NewReport from "./NewReport";
import OBARReport from "./OBARReport";
import OnBehalfOfSchedule from "./OnBehalfOfSchedule";
import OPSRReport from "./OPSRReport";
import Patient from "./Patient";
import PatientsList from "./PatientsList/PatientsList";
import ProgressNoteList from "./PatientProfileView/ProgressNoteList";
import ProviderProfile from "./ProviderProfile";
import Ratings from "./PatientProfileView/Ratings";
import ReferralView from "./ReferralView";
import Reports from "./Reports";
// eslint-disable-next-line import-x/no-named-as-default
import SchedulePageV2 from "./SchedulePageV2";
import ViewDraftNote from "./PatientProfileView/ViewDraftNote";
import ViewDraftNoteV2 from "./PatientProfileView/ViewDraftNoteV2";
import ViewNote from "./PatientProfileView/ViewNote";

class ProviderRouter extends Component {
  // Authorization flags (computed at class initialization)
  get isCaseManagerOrPeer() {
    return isAuthorized(provider_case_manager) || isAuthorized(provider_peer);
  }

  get isPcp() {
    return isAuthorized(provider_pcp);
  }

  get isPeer() {
    return isAuthorized(provider_peer);
  }

  get isBehavioral() {
    return isAuthorized(provider_behavioral);
  }

  ROUTE_SECTION_OPTIONS = this.determineNavBarSelection();

  PROVIDER_BASE_REDIRECT_ROUTE = this.isCaseManagerOrPeer
    ? `${providerRoute}/patient`
    : `${providerRoute}/home`;

  ACTION_SECTION_OPTIONS = [];

  navbarConfiguration = {
    customRenderSection: [
      {
        componentProps: {
          config: {
            options: this.ACTION_SECTION_OPTIONS,
          },
        },
        isComponent: true,
        render: ActionSection,
      },
    ],
    routeSection: {
      options: this.ROUTE_SECTION_OPTIONS,
    },
    selfSection: {},
  };

  componentDidMount() {
    redirectOnSignIn(this.props.history);
    this.props.getProviderProfile();
  }

  determineNavBarSelection() {
    if (this.isPcp) {
      return [
        {
          activeIconComponent: mapLabelToComponent("Dashboard", true),
          icon: "NavMenu__dashboard-icon",
          iconComponent: mapLabelToComponent("Dashboard"),
          label: "Dashboard",
          mobileIcon: "dashboard.svg",
          route: this.props.url + "/home",
        },
        {
          activeIconComponent: mapLabelToComponent("Patients", true),
          icon: "NavMenu__patients-icon",
          iconComponent: mapLabelToComponent("Patients"),
          label: "Patients",
          mobileIcon: "patients.svg",
          route: this.props.url + "/patient",
        },
        {
          activeIconComponent: mapLabelToComponent("Patients", true),
          icon: "NavMenu__patients-icon",
          iconComponent: mapLabelToComponent("Patients"),
          label: "Add New Patient",
          mobileIcon: "add_new.svg",
          mobileOnly: true,
          route: this.props.url + "/patient/add",
        },
        {
          activeIconComponent: mapLabelToComponent("Reports", true),
          icon: "NavMenu__reports-icon",
          iconComponent: mapLabelToComponent("Reports"),
          label: "Reports",
          mobileIcon: "reports.svg",
          permissionKey: "reports",
          route: this.props.url + "/reports",
        },
        {
          activeIconComponent: mapLabelToComponent("Screening", true),
          icon: "NavMenu__screening-icon",
          iconComponent: mapLabelToComponent("Screening"),
          label: "Screening",
          mobileIcon: "screening.svg",
          route: this.props.url + "/a5_screenings",
        },
        {
          activeIconComponent: mapLabelToComponent("Settings", true),
          icon: "NavMenu__settings-icon",
          iconComponent: mapLabelToComponent("Settings"),
          label: "Settings",
          mobileIcon: "settings.svg",
          route: this.props.url + "/settings",
        },
      ];
    }

    if (this.isCaseManagerOrPeer) {
      return [
        {
          activeIconComponent: mapLabelToComponent("Patients", true),
          icon: "NavMenu__patients-icon",
          iconComponent: mapLabelToComponent("Patients"),
          label: "Patients",
          mobileIcon: "patients.svg",
          route: this.props.url + "/patient",
        },
        {
          activeIconComponent: mapLabelToComponent("Reports", true),
          icon: "NavMenu__reports-icon",
          iconComponent: mapLabelToComponent("Reports"),
          label: "Reports",
          mobileIcon: "reports.svg",
          permissionKey: "reports",
          route: this.props.url + "/reports",
        },
        {
          activeIconComponent: mapLabelToComponent("Messages", true),
          iconComponent: mapLabelToComponent("Messages"),
          label: "Messages",
          route: this.props.url + "/messages",
        },
      ];
    }

    return [
      {
        activeIconComponent: mapLabelToComponent("Dashboard", true),
        icon: "NavMenu__dashboard-icon",
        iconComponent: mapLabelToComponent("Dashboard"),
        label: "Dashboard",
        mobileIcon: "dashboard.svg",
        route: this.props.url + "/home",
      },
      {
        activeIconComponent: mapLabelToComponent("Patients", true),
        icon: "NavMenu__patients-icon",
        iconComponent: mapLabelToComponent("Patients"),
        label: "Patients",
        mobileIcon: "patients.svg",
        route: this.props.url + "/patient",
      },
      {
        activeIconComponent: mapLabelToComponent("Reports", true),
        icon: "NavMenu__reports-icon",
        iconComponent: mapLabelToComponent("Reports"),
        label: "Reports",
        mobileIcon: "reports.svg",
        permissionKey: "reports",
        route: this.props.url + "/reports",
      },
      {
        activeIconComponent: mapLabelToComponent("Calendar", true),
        iconComponent: mapLabelToComponent("Calendar"),
        label: "Calendar",
        route: this.props.url + "/schedule",
      },
      {
        activeIconComponent: mapLabelToComponent("Messages", true),
        iconComponent: mapLabelToComponent("Messages"),
        label: "Messages",
        route: this.props.url + "/messages",
      },
    ];
  }

  render() {
    let baseUrl = this.props.url;

    // Authorization flags for render logic
    const isCaseManagerOrPeer = this.isCaseManagerOrPeer;
    const isPcp = this.isPcp;
    const isPeer = this.isPeer;
    const isBehavioral = this.isBehavioral;
    const providerType = this.props.profile?.provider_type;
    const isBehavioralIntake = isBehavioral && providerType === "BEHAVIORAL_INTAKE";

    // Fresh reference on each render to trigger renders downstream once profile loads
    const routeOptions = [...this.ROUTE_SECTION_OPTIONS];

    if (_.get(this.props, "profile.omnisearch_enabled")) {
      if (!routeOptions.some((option) => option.label === "Search")) {
        routeOptions.push({
          activeIconComponent: mapLabelToComponent("Search", true),
          iconComponent: mapLabelToComponent("Search"),
          label: "Search",
        });
      }
    }

    const navbarConfiguration = {
      customRenderSection: [
        {
          componentProps: {
            config: {
              options: this.ACTION_SECTION_OPTIONS,
            },
          },
          isComponent: true,
          render: ActionSection,
        },
      ],
      routeSection: {
        options: routeOptions,
      },
      selfSection: {},
    };

    const showChangePassword = this.props.profile && !this.props.profile.idp;

    return (
      <>
        <Toolbar
          baseRoute={providerRoute}
          isMessagingEnabled={!isPcp}
          navbarConfiguration={navbarConfiguration}
        />

        <Main2>
          {/* CHANGING ROUTE ORDER CAN LEAD TO UNFORESEEN BEHAVIOR AND NEEDS TO BE TESTED */}
          <Switch>
            {!isCaseManagerOrPeer && (
              <Route
                path={baseUrl + "/home"}
                render={(props) => <ProviderHome {...props} {...this.props} />}
              />
            )}

            <Route component={Patient} path={baseUrl + "/patient/add"} />

            <ProtectedRoute
              component={NewNoteV2}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/note/add/v2"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={NewNote}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/note/add"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={ViewDraftNoteV2}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/note/:noteId/v2/draft"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={ViewDraftNote}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/note/:noteId/draft"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={ViewFullNote}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/note/:noteId/v2"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={PrintNoteView}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/export_notes"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={ViewNote}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/note/:noteId"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={ProgressNoteList}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/note"}
              permissionKey="patientDiagnosis"
            />

            {!isPeer && (
              <ProtectedRoute
                component={CareCommunicationForm}
                fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
                path={baseUrl + "/patient/:patientId/care_communication"}
                permissionKey="patientDiagnosis"
              />
            )}

            {!isPeer && (
              <ProtectedRoute
                component={CaseManagementForm}
                fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
                path={baseUrl + "/patient/:patientId/case_management"}
                permissionKey="patientDiagnosis"
              />
            )}

            {!isPeer && (
              <ProtectedRoute
                component={CareManagementNewForm}
                fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
                path={baseUrl + "/patient/:patientId/care_management"}
                permissionKey="patientDiagnosis"
              />
            )}

            {canCreateAdvancedCareActivityNotes(providerType) && (
              <Route
                component={AdvancedCareActivityNewForm}
                path={baseUrl + "/patient/:patientId/advanced_care_activity"}
              />
            )}

            <Route
              component={ClinicalAssessment}
              path={baseUrl + "/patient/:patientId/clinical_assessment"}
            />

            <ProtectedRoute
              component={MemberProfile}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/profile/v2"}
              permissionKey="patientDiagnosis"
            />

            <ProtectedRoute
              component={CareTeamList}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/patient/:patientId/care_team"}
              permissionKey="patientDiagnosis"
            />

            <Route component={IBARReport} path={baseUrl + "/patient/:patientId/report/ibar"} />
            <Route component={IPSRReport} path={baseUrl + "/patient/:patientId/report/ipsr"} />
            <Route component={OBARReport} path={baseUrl + "/patient/:patientId/report/obar"} />
            <Route component={OPSRReport} path={baseUrl + "/patient/:patientId/report/opsr"} />
            <Route component={A5Report} path={baseUrl + "/patient/:patientId/report/a5"} />
            <Route component={Reports} path={baseUrl + "/patient/:patientId/report"} />

            <Route component={Ratings} path={baseUrl + "/patient/:patientId/ratings"} />
            <Route component={PHQ9} path={baseUrl + "/patient/:patientId/phq-9"} />
            <Route component={GAD7} path={baseUrl + "/patient/:patientId/gad-7"} />

            <Route
              path={baseUrl + "/patient/:patientId/file"}
              render={(props) => <FileList {...props} type="patient" />}
            />

            <Route
              component={DischargeData}
              path={baseUrl + "/patient/:patientId/discharge/:dischargeId"}
            />

            {isBehavioralIntake && (
              <Route
                component={WaitlistForm}
                path={baseUrl + "/patient/:patientId/waitlist_form"}
              />
            )}

            <Route component={Patient} path={baseUrl + "/patient/:patientId"} />
            <Route component={PatientsList} path={baseUrl + "/patient"} />

            <Route component={ProviderProfile} path={baseUrl + "/profile"} />

            {!isCaseManagerOrPeer && (
              <Route component={EditEvent} path={baseUrl + "/schedule/event"} />
            )}

            {!isCaseManagerOrPeer && (
              <Route component={AddAppointment} path={baseUrl + "/schedule/add"} />
            )}

            {isCaseManagerOrPeer ? (
              <Route
                path={baseUrl + "/schedule/:providerId"}
                render={(props) => <SchedulePageV2 {...props} />}
              />
            ) : (
              <Route component={OnBehalfOfSchedule} path={baseUrl + "/schedule/:providerId"} />
            )}

            {!isCaseManagerOrPeer && (
              <Route
                path={baseUrl + "/schedule"}
                render={(props) => <SchedulePageV2 {...props} />}
              />
            )}

            {showChangePassword && (
              <Route
                path={baseUrl + "/change-password"}
                render={(props) => <ChangePassword {...props} baseRoute={providerRoute} />}
              />
            )}

            <Route
              path={baseUrl + "/settings"}
              render={() => <Settings baseRoute={providerRoute} />}
            />

            <Route
              path={baseUrl + "/privacy-policy"}
              render={() => <PrivacyPolicy baseRoute={providerRoute} />}
            />

            <Route
              path={baseUrl + "/tutorial"}
              render={() => <TutorialScreens baseRoute={providerRoute} />}
            />

            <Route component={SystemCheck} path={baseUrl + "/system-check"} />
            <Route component={AVCheck} path={baseUrl + "/av-check"} />
            <Route component={VideoClient} path={baseUrl + "/video-call"} />

            <ProtectedRoute
              component={NewReport}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/reports/add"}
              permissionKey="reports"
            />

            <ProtectedRoute
              component={Reports}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/reports"}
              permissionKey="reports"
            />

            {!isPcp && <Route component={MessagesView} path={baseUrl + "/messages"} />}

            {isPcp && (
              <Route
                path={baseUrl + "/a5_screenings/:screening_id/file"}
                render={(props) => <FileList {...props} type="a5_screenings" />}
              />
            )}

            {isPcp && (
              <Route
                component={A5ScreeningDetails}
                path={baseUrl + "/a5_screenings/:screening_id"}
              />
            )}

            {isPcp && <Route component={A5ScreeningList} path={baseUrl + "/a5_screenings"} />}

            <Route
              path={baseUrl + "/referral/:referralCode/file"}
              render={(props) => <FileList {...props} type="referral" />}
            />

            <Route component={ReferralView} path={baseUrl + "/referral/:referralCode"} />

            <ProtectedRoute
              component={DraftNotesTable}
              fallbackRoute={this.PROVIDER_BASE_REDIRECT_ROUTE}
              path={baseUrl + "/draft-notes"}
              permissionKey="patientDiagnosis"
            />

            <Redirect from={baseUrl} to={this.PROVIDER_BASE_REDIRECT_ROUTE} />
          </Switch>
        </Main2>

        <Footer />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
  };
};

ProviderRouter.propTypes = {
  getProviderProfile: PropTypes.func,
  history: PropTypes.object,
  profile: PropTypes.object,
  url: PropTypes.string,
};

export default connect(mapStateToProps, { getProviderProfile })(withRouter(ProviderRouter));
